// src/App.js
import React, { useEffect, useState, useMemo } from "react";
import "./App.css";
import ProductSEO from "./ProductSEO";
import { Routes, Route, Link } from "react-router-dom";
import BlogList from "./pages/BlogList";
import BlogPost from "./pages/BlogPost";

// 👉 Put your actual Apps Script Web App URL here:
const API_URL =
  "https://script.google.com/macros/s/AKfycbyO4xbYPdkqYrQo13tLuyRTZM61lxfsS7hzPhTAOziVsjRmtzqYi6w5Fi5QT6syXIYx/exec";

const PRODUCTS_PER_PAGE = 24;

// Helper: convert YouTube URL to embed URL
function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  try {
    const str = String(url).trim();
    const regex =
      /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([^?&/]+)/;
    const match = str.match(regex);
    const videoId = match ? match[1] : null;
    if (!videoId) return null;

    return `https://www.youtube.com/embed/${videoId}?autoplay=0&mute=0&controls=1&rel=0&playsinline=1`;
  } catch (e) {
    return null;
  }
}
function getYouTubeHeroEmbedUrl(url) {
  if (!url) return null;
  try {
    const str = String(url).trim();
    const regex =
      /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([^?&/]+)/;
    const match = str.match(regex);
    const videoId = match ? match[1] : null;
    if (!videoId) return null;

    // Autoplay, muted, looped embed
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&rel=0&playsinline=1&loop=1&playlist=${videoId}`;
  } catch (e) {
    return null;
  }
}
function getYouTubeId(url) {
  if (!url) return null;
  const regex =
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([^?&/]+)/;
  const m = String(url).match(regex);
  return m ? m[1] : null;
}
// Helper to create page list with ellipses (maxButtons controls compactness)
function getPageNumbers(totalPages, currentPage, maxButtons = 7) {
  const pages = [];
  if (totalPages <= maxButtons) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return pages;
  }

  // always include first and last
  const left = Math.max(2, currentPage - 1);
  const right = Math.min(totalPages - 1, currentPage + 1);

  pages.push(1);

  if (left > 2) pages.push("left-ellipsis");

  for (let i = left; i <= right; i++) pages.push(i);

  if (right < totalPages - 1) pages.push("right-ellipsis");

  pages.push(totalPages);
  return pages;
}

/* ================= ROUTER WRAPPER ================= */

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
    </Routes>
  );
}

/* sample testimonials — edit these manually or replace with a fetch to your sheet later */
const SAMPLE_TESTIMONIALS = [
  {
    id: "t1",
    name: "Riya Sharma",
    text: "Amazing quality and very fast delivery. My kids love the die-cast cars!",
    rating: 5,
    source: "Amazon",
  },
  {
    id: "t2",
    name: "Anil Verma",
    text: "Great customer support and packaging. Will buy again from Toyco.",
    rating: 5,
    source: "Flipkart",
  },
  {
    id: "t3",
    name: "Meera Patel",
    text: "The product is exactly as described and the video helped a lot before purchase.",
    rating: 4,
    source: "Amazon",
  },
  {
    id: "t4",
    name: "Suhas Rao",
    text: "Superb build quality — feels premium for the price.",
    rating: 5,
    source: "Flipkart",
  },
];

function ReviewsTestimonials({
  startCount = 20000,
  suffix = "+ reviews across marketplaces",
  yearsExperience = "10+ years",
}) {
  // testimonials state (you can later replace with fetched array)
  const [testimonials] = useState(SAMPLE_TESTIMONIALS);

  // compute average rating from testimonials
  const avgRating = testimonials.length
    ? testimonials.reduce((s, t) => s + (Number(t.rating) || 0), 0) /
      testimonials.length
    : 0;
  const avgRounded = Math.round(avgRating * 10) / 10;

  // counter animation
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = startCount;
    const duration = 1200; // ms
    let startTime = null;
    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const val = Math.floor(progress * (end - start) + start);
      setCount(val);
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }
    requestAnimationFrame(step);
  }, [startCount]);

  // simple carousel index
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setIdx((i) => (i + 1) % testimonials.length),
      4500
    );
    return () => clearInterval(t);
  }, [testimonials.length]);

  return (
    <section
      id="reviews-testimonials"
      className="section section-alt testimonials-section"
    >
      <div
        className="testimonials-inner"
        style={{ maxWidth: 1100, margin: "0 auto" }}
      >
        <div className="testimonials-top" style={{ alignItems: "flex-start" }}>
          <div className="reviews-counter">
            <div className="counter-number">{count.toLocaleString()}</div>
            <div className="counter-sub">{suffix}</div>

            <div className="experience-badge">
              <strong>{yearsExperience}</strong> experience
            </div>
          </div>

          <div
            className="marketplace-badges-row"
            style={{ alignItems: "flex-start" }}
          >
            <div className="rating-box">
              <div className="rating-stars">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span
                    key={i}
                    className={i < Math.round(avgRating) ? "star on" : "star"}
                  >
                    ★
                  </span>
                ))}
              </div>
              <div className="rating-number">{avgRounded} / 5</div>
              <div className="rating-meta">
                {testimonials.length} testimonials
              </div>
            </div>

            <div className="badge">
              <img
                src={require("./assets/amazon-choice.webp")}
                alt="Amazon's Choice"
              />
              <div className="badge-caption">Amazon's Choice</div>
            </div>

            <div className="badge">
              <img
                src={require("./assets/flipkart-assured.jpg")}
                alt="Flipkart Bestseller"
              />
              <div className="badge-caption">Flipkart Bestseller</div>
            </div>
          </div>
        </div>

        <div className="testimonials-carousel-wrapper">
          <button
            className="carousel-arrow left"
            onClick={() =>
              setIdx((i) => (i - 1 + testimonials.length) % testimonials.length)
            }
          >
            ‹
          </button>

          <div className="testimonials-carousel">
            {testimonials.map((t, i) => (
              <div
                key={t.id}
                className={`testimonial-card ${i === idx ? "active" : ""}`}
                aria-hidden={i === idx ? "false" : "true"}
              >
                <div className="testimonial-body">“{t.text}”</div>
                <div className="testimonial-meta">
                  <div className="testimonial-name">{t.name}</div>
                  <div className="testimonial-source">{t.source}</div>
                </div>
                <div className="testimonial-stars">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <span
                      key={s}
                      className={s < Math.round(t.rating) ? "star on" : "star"}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <button
            className="carousel-arrow right"
            onClick={() => setIdx((i) => (i + 1) % testimonials.length)}
          >
            ›
          </button>
        </div>
      </div>
    </section>
  );
}
function Home() {
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1); // current page number
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Filter states
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [ageFilter, setAgeFilter] = useState("All");
  const [priceFilter, setPriceFilter] = useState("All");

  // Back-to-top state
  const [showBackToTop, setShowBackToTop] = useState(false);
  // toggle visibility
  useEffect(() => {
    function check() {
      setShowBackToTop(window.scrollY > 400);
    }
    window.addEventListener("scroll", check, { passive: true });
    check();
    return () => window.removeEventListener("scroll", check);
  }, []);

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  // Modal state
  const [modalProduct, setModalProduct] = useState(null);

  function openProductModal(product) {
    setModalProduct(product);
  }

  function closeProductModal() {
    setModalProduct(null);
  }

  useEffect(() => {
    function onScroll() {
      const navbar = document.querySelector(".navbar");
      if (!navbar) return;
      if (window.scrollY > 20) {
        navbar.classList.add("scrolled");
      } else {
        navbar.classList.remove("scrolled");
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Fetch products once on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchProducts() {
      try {
        setLoading(true);

        // Immediately show skeletons (UI) — we set loading true above
        // Fetch the entire dataset. (If later we add '?limit=12' support in backend, we can fetch smaller.)
        const resp = await fetch(API_URL);
        if (!resp.ok) throw new Error("Network response was not ok");

        const data = await resp.json();
        if (cancelled) return;

        setProducts(data.products || []);
        // setDisplayCount(PRODUCTS_PER_PAGE); // show first page
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Could not load products. Please try again later.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, []); // empty deps: run once

  // Unique categories for filter dropdown
  const categoryOptions = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      if (p.category && String(p.category).trim() !== "") {
        set.add(String(p.category).trim());
      }
    });
    return ["All", ...Array.from(set)];
  }, [products]);

  // Unique age groups for filter dropdown
  const ageOptions = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      if (p.ageGroup && String(p.ageGroup).trim() !== "") {
        set.add(String(p.ageGroup).trim());
      }
    });
    return ["All", ...Array.from(set)];
  }, [products]);

  // Filter logic — apply filters to the currently fetched products
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      // Category filter
      if (
        categoryFilter !== "All" &&
        String(p.category).trim() !== categoryFilter
      )
        return false;
      // Age filter
      if (ageFilter !== "All" && String(p.ageGroup).trim() !== ageFilter)
        return false;
      // Price filter
      if (priceFilter !== "All") {
        const priceNum = Number(String(p.price || "").replace(/[^\d.]/g, ""));
        if (!priceNum || isNaN(priceNum)) return false;
        if (priceFilter === "below500" && !(priceNum < 500)) return false;
        if (priceFilter === "500to999" && !(priceNum >= 500 && priceNum <= 999))
          return false;
        if (priceFilter === "1000plus" && !(priceNum >= 1000)) return false;
      }
      return true;
    });
  }, [products, categoryFilter, ageFilter, priceFilter]);

  // Items to display (paged)
  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE)
  );
  const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const visibleProducts = filteredProducts.slice(
    startIndex,
    startIndex + PRODUCTS_PER_PAGE
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, ageFilter, priceFilter]);
  // Products that have a video URL → for Video Gallery
  const galleryVideos = useMemo(() => {
    return products.filter(
      (p) =>
        p.videoUrl &&
        String(p.videoUrl).trim() !== "" &&
        getYouTubeEmbedUrl(p.videoUrl)
    );
  }, [products]);
  function goToPage(page) {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    // smooth scroll to products section for better UX
    const el = document.getElementById("products");
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 80; // 80px offset for navbar
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }
  // Hero slides: take first 3 products with video + main image
  const heroSlides = useMemo(() => {
    return galleryVideos.slice(0, 3).map((p) => {
      // choose image
      let image = p.mainImageUrl;
      if (!image || String(image).trim() === "") {
        if (p.extraImagesCsv) {
          const parts = String(p.extraImagesCsv)
            .split(",")
            .map((u) => u.trim())
            .filter((u) => u.length > 0);
          if (parts.length > 0) image = parts[0];
        }
      }

      return {
        name: p.name || "Toyco Toy",
        category: p.category || "",
        ageGroup: p.ageGroup || "",
        price: p.price || "",
        videoUrl: p.videoUrl,
        imageUrl: image || null,
        marketplaceUrl: p.marketplaceUrl || "",
      };
    });
  }, [galleryVideos]);

  return (
    <div className="app">
      {/* Top navbar */}
      <header className="navbar">
        <div className="logo-wrap">
          <Link to="/" className="logo-link">
            <img
              src={require("./assets/logo.png")}
              alt="Toyco Logo"
              className="logo-img"
            />
          </Link>
          <span className="logo-tagline">Play Well & Grow Well</span>
        </div>
        <nav className="nav-links">
          <a href="#home">Home</a>
          <a href="#products">Products</a>
          <a href="#gallery">Videos</a>
          <a href="#brand">Our Story</a>
          <a href="#marketplaces">Where to Buy</a>
          <a href="#contact">Contact</a>
          <Link to="/blog" className="nav-link">
            Blog
          </Link>
        </nav>
      </header>
      {/* Hero / Home section with carousel */}
      <section id="home" className="section hero-section">
        {/* Show skeleton while initial data is loading */}
        {loading ? (
          <div className="hero-skeleton" aria-hidden="true">
            <div className="hero-skeleton-left" />
            {/* blurred logo placeholder */}
            <img
              src={require("./assets/logo.png")}
              alt="Toyco logo"
              className="hero-skeleton-logo"
            />
            <div className="hero-skeleton-logo-overlay" />
            <div className="hero-skeleton-right">
              <div className="skeleton-line big" />
              <div className="skeleton-line medium" />
              <div className="skeleton-line small" />
            </div>
          </div>
        ) : heroSlides.length > 0 ? (
          <HeroCarousel slides={heroSlides} />
        ) : (
          // If not loading and no hero slides, show the gentle welcome fallback
          <div className="hero">
            <div className="hero-content">
              <h1>Welcome to Toyco</h1>
              <p className="hero-subtitle">
                Fun, safe and exciting toys for kids of all ages. This is the
                official Toyco catalogue – discover our latest products and
                collections.
              </p>
              <a href="#products">
                <button className="primary-btn">View Our Products</button>
              </a>
            </div>
          </div>
        )}
      </section>

      {/* Products section */}
      <section id="products" className="section">
        <h2>Our Products</h2>
        {loading && <p className="loading-note">Loading products…</p>}
        {error && <p className="error-text">{error}</p>}

        {!loading && !error && products.length > 0 && (
          <div className="filters">
            <div className="filters-group">
              <label>Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                {categoryOptions.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div className="filters-group">
              <label>Age Group</label>
              <select
                value={ageFilter}
                onChange={(e) => setAgeFilter(e.target.value)}
              >
                {ageOptions.map((age) => (
                  <option key={age} value={age}>
                    {age}
                  </option>
                ))}
              </select>
            </div>

            <div className="filters-group">
              <label>Price Range</label>
              <select
                value={priceFilter}
                onChange={(e) => setPriceFilter(e.target.value)}
              >
                <option value="All">All</option>
                <option value="below500">Below ₹500</option>
                <option value="500to999">₹500 – ₹999</option>
                <option value="1000plus">₹1000 and above</option>
              </select>
            </div>
          </div>
        )}

        {!loading && !error && products.length === 0 && (
          <p>No products found. Please check your Google Sheet.</p>
        )}

        {!loading &&
          !error &&
          products.length > 0 &&
          filteredProducts.length === 0 && (
            <p>No products match the selected filters.</p>
          )}

        {/* PRODUCT GRID */}
        <div className="product-grid">
          {/* Show skeletons while loading (exactly PRODUCTS_PER_PAGE count) */}
          {loading &&
            Array.from({ length: PRODUCTS_PER_PAGE }).map((_, i) => (
              <div className="product-card skeleton" key={"skeleton-" + i}>
                <div className="skeleton-image" />
                <div className="skeleton-line short" />
                <div className="skeleton-line" />
                <div className="skeleton-line tiny" />
              </div>
            ))}

          {/* Render visible products */}
          {!loading &&
            visibleProducts.map((product, index) => (
              <ProductCard
                key={index}
                product={product}
                onOpen={openProductModal}
              />
            ))}
        </div>

        {/* PAGINATION CONTROLS */}
        {!loading && filteredProducts.length > PRODUCTS_PER_PAGE && (
          <div className="pagination" role="navigation" aria-label="Pagination">
            <button
              className="pagination-btn"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ‹ Prev
            </button>

            {getPageNumbers(totalPages, currentPage, 7).map((p, idx) =>
              typeof p === "number" ? (
                <button
                  key={p}
                  className={`pagination-btn ${
                    p === currentPage ? "active" : ""
                  }`}
                  onClick={() => goToPage(p)}
                >
                  {p}
                </button>
              ) : (
                <span key={"e" + idx} className="pagination-ellipsis">
                  …
                </span>
              )
            )}

            <button
              className="pagination-btn"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next ›
            </button>
          </div>
        )}
      </section>

      {/* Video Gallery section */}
      {!loading && !error && galleryVideos.length > 0 && (
        <section id="gallery" className="section section-alt">
          <h2>Video Gallery</h2>
          <p>
            Short vertical videos showcasing Toyco toys in action. These are
            powered by the Video URL column in your Google Sheet.
          </p>
          <VideoGallery videos={galleryVideos} />
        </section>
      )}

      {/* Brand Story */}
      <section id="brand" className="section section-alt">
        <h2>Our Story</h2>
        <p>
          Toyco started with a simple promise: give every child a toy they will
          remember. Over more than 10+ years in the toy industry, our family has
          handled thousands of boxes, tested countless samples and listened
          carefully to parents, kids and sellers across India. What began as a
          small trading operation has slowly grown into a focused brand built
          around quality, value and trust.
        </p>
        <p>
          We specialise in toys that are exciting for kids and sensible for
          parents – diecast cars, pull-back vehicles, remote control toys and
          early learning toys that spark curiosity without being fragile or
          unsafe. Every Toyco product that you see in our catalogue has been
          shortlisted from many options. We look at build quality, moving parts,
          detailing, battery compartments and edge finishing before we decide to
          put our name on it.
        </p>
        <p>
          Because Toyco is deeply connected to marketplaces like Amazon,
          Flipkart and other platforms, we get daily feedback through customer
          reviews, ratings and return reasons. That data doesn’t just sit in a
          report. We use it to refine our catalogue, improve packaging and
          remove designs that don’t meet our expectations. Over time, this has
          helped us build a tight, high-performing range that parents repeatedly
          come back to.
        </p>
        <p>
          At the same time, we still operate like a small, responsive family
          business. When a seller or parent reaches out to us, they are not
          talking to a big call centre – they are talking to people who handle
          these products every single day. We understand the reality of Indian
          homes, festival gifting and birthday budgets, and we design our
          assortment keeping all of that in mind.
        </p>
        <p>
          The Toyco catalogue website at toycotoys.com is our official space to
          showcase current and upcoming products, videos, features and
          marketplace availability. Think of it as a live showroom that keeps
          evolving – new toys, new colours, new demos – all curated in one
          place. Whether you are a parent, a reseller or a partner brand, we
          want this catalogue to make it easy for you to explore, shortlist and
          share Toyco toys with confidence.
        </p>
        <h2>Our Vision</h2>
        <p>
          Our vision is simple: every time a child opens a Toyco box, they
          should feel excitement, not disappointment. As we grow, we will keep
          investing in better designs, clearer communication and stronger
          quality checks so that Toyco remains a brand you are proud to bring
          into your home or your business.
        </p>
      </section>

      {/* Marketplaces */}
      <section id="marketplaces" className="section">
        <h2>Where You Can Find Toyco</h2>
        <p>Toyco products are available on leading online marketplaces.</p>
        <div className="marketplace-badges">
          <a
            href="https://www.amazon.in/stores/Toyco/page/9B3F552B-CD74-4A58-A080-A848FDA0453B?lp_asin=B0FC5W18B9&ref_=ast_bln&store_ref=bl_ast_dp_brandLogo_sto"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="badge">Amazon</div>
          </a>
          <a
            href="https://www.flipkart.com/search?q=toyco&otracker=search&otracker1=search&marketplace=FLIPKART&as-show=on&as=off&as-pos=1&as-type=HISTORY"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="badge">Flipkart</div>
          </a>
          <div className="badge">Jiomart</div>
          <div className="badge">Blinkit</div>
          <div className="badge">Meesho</div>
          <div className="badge">ONDC</div>
        </div>
        <p className="note">
          Click On The Marketplace Badge To See All Our Deals.
        </p>
      </section>

      <ReviewsTestimonials
        startCount={20000}
        suffix="+ reviews across marketplaces"
        yearsExperience="10+ years"
      />
      <section id="faq" className="section section-alt">
        <h2>Frequently Asked Questions</h2>

        <div className="faq-item">
          <h3>Are Toyco toys safe for kids?</h3>
          <p>
            Yes. We shortlist toys only after checking build quality, small
            parts, edges and battery compartments. We focus on toys that are
            suitable for Indian homes and follow safety checks during sourcing
            and packing.
          </p>
        </div>

        <div className="faq-item">
          <h3>Where can I buy Toyco toys?</h3>
          <p>
            Toyco toys are available on leading marketplaces such as Amazon,
            Flipkart, Jiomart, Veer Traders, DigiHaat. This catalogue at
            toycotoys.com is for browsing products, videos and details – the
            actual purchase happens on these marketplaces.
          </p>
        </div>

        <div className="faq-item">
          <h3>Do you offer warranty or returns?</h3>
          <p>
            Return and replacement policies are handled directly by the
            marketplace where you purchase the toy. However, we actively review
            all return reasons and work to improve our products and packaging
            based on that feedback.
          </p>
        </div>

        <div className="faq-item">
          <h3>Can I place bulk or wholesale orders?</h3>
          <p>
            Yes. For bulk, corporate or reseller enquiries, you can reach out to
            us through the contact section or via WhatsApp. We work with offline
            retailers, marketplace sellers and gifting partners.
          </p>
        </div>

        <div className="faq-item">
          <h3>How often do you add new toys?</h3>
          <p>
            We update our catalogue regularly based on marketplace performance,
            seasonal demand and new designs from our suppliers. New arrivals are
            generally added every few weeks, and we keep the Google Sheet that
            powers this site in sync.
          </p>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="section section-alt">
        <h2>Contact & Business Enquiries</h2>
        <p>
          For bulk orders, distribution, or any business enquiry, you can reach
          us at:
        </p>
        <ul>
          <li>
            Email: <strong>care.toyco@gmail.com</strong>
          </li>
          <li>
            WhatsApp: <strong>+91-9625783388</strong>
          </li>
          <li>
            Address: <strong>Chhota Bazar Shahdara, Delhi-110032</strong>
          </li>
        </ul>
      </section>
      {modalProduct && (
        <ProductModal product={modalProduct} onClose={closeProductModal} />
      )}

      {showBackToTop && (
        <button
          className="back-to-top"
          onClick={scrollToTop}
          aria-label="Back to top"
        >
          ↑
        </button>
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="social-links">
          <a
            href="https://instagram.com/toyco.official"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg className="social-svg instagram" viewBox="0 0 24 24">
              <path d="M7 2C4.24 2 2 4.24 2 7v10c0 2.76 2.24 5 5 5h10c2.76 0 5-2.24 5-5V7c0-2.76-2.24-5-5-5H7zm10 2c1.65 0 3 1.35 3 3v10c0 1.65-1.35 3-3 3H7c-1.65 0-3-1.35-3-3V7c0-1.65 1.35-3 3-3h10zm-5 3a5 5 0 100 10 5 5 0 000-10zm0 2a3 3 0 110 6 3 3 0 010-6zm4.5-.9a1.1 1.1 0 100 2.2 1.1 1.1 0 000-2.2z" />
            </svg>
          </a>

          <a
            href="https://youtube.com/@toyzhome7949"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg className="social-svg youtube" viewBox="0 0 24 24">
              <path d="M19.6 3.2H4.4C2.9 3.2 2 4.4 2 6v12c0 1.5.9 2.8 2.4 2.8h15.2c1.5 0 2.4-1.3 2.4-2.8V6c0-1.6-.9-2.8-2.4-2.8zM10 15.8V8.2l6.2 3.8L10 15.8z" />
            </svg>
          </a>

          <a
            href="https://facebook.com/yourpage"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg className="social-svg facebook" viewBox="0 0 24 24">
              <path d="M13 3h4V0h-4c-3.3 0-6 2.7-6 6v3H4v4h3v11h4V13h3l1-4h-4V6c0-1.1.9-2 2-2z" />
            </svg>
          </a>

          <a
            href="https://wa.me/919625783388"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg className="social-svg whatsapp" viewBox="0 0 24 24">
              <path d="M20.5 3.5A11.78 11.78 0 0012 0 11.78 11.78 0 003.5 3.5 11.78 11.78 0 000 12a11.8 11.8 0 001.8 6.2L0 24l6-1.8A11.73 11.73 0 0012 24a11.78 11.78 0 008.5-3.5A11.78 11.78 0 0024 12c0-3.2-1.2-6.2-3.5-8.5zM12 21.5c-1.8 0-3.6-.5-5.2-1.5l-.4-.3-3.5 1 1-3.5-.3-.4c-1-1.6-1.5-3.4-1.5-5.3 0-5.4 4.4-9.8 9.8-9.8S21.7 6.6 21.7 12 17.4 21.5 12 21.5zm5-7.3c-.3-.1-1.7-.8-1.9-.9-.3-.1-.5-.1-.7.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.4-.5-2.6-1.7-1-1-1.7-2.3-1.8-2.7-.2-.2 0-.4.1-.6.1-.1.3-.4.5-.7.2-.2.2-.4.2-.6 0-.1-.1-.5-.2-.7-.2-.2-.7-1.7-.9-2.4-.2-.7-.5-.6-.7-.6h-.6c-.2 0-.6.1-.9.4C6.1 7.9 5.8 8.7 5.8 9c0 .3.1.7.2 1 .3 1.2 1 2.3 1.9 3.3 1.1 1.3 2.6 2.3 4.1 2.9 1.6.6 2.3.6 3.1.5.5-.1 1.7-.7 1.9-1.3.2-.6.2-1.1.1-1.2-.1-.3-.3-.4-.6-.5z" />
            </svg>
          </a>
          <a
            href="https://x.com/yourprofile"
            target="_blank"
            rel="noopener noreferrer"
            className="x-icon"
          >
            <svg className="social-svg" viewBox="0 0 24 24">
              <path d="M18.244 2H21.5l-7.43 8.49L23 22h-5.818l-4.55-5.958L6.45 22H3l7.86-9.01L1 2h5.956l4.124 5.551L18.244 2zm-1.984 17.66h1.742L8.37 4.24H6.5l9.76 15.42z" />
            </svg>
          </a>
        </div>

        <p>© {new Date().getFullYear()} Toyco®. All Rights Reserved.</p>
      </footer>
    </div>
  );
}

// HERO CAROUSEL COMPONENT
function HeroCarousel({ slides }) {
  const [current, setCurrent] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // small timeout so skeleton->hero flip feels smooth
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);
  // Auto-advance slides every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const activeSlide = slides[current];
  const heroEmbedUrl = getYouTubeHeroEmbedUrl(activeSlide.videoUrl);

  const priceText =
    activeSlide.price && String(activeSlide.price).trim() !== ""
      ? `Starting at ₹${String(activeSlide.price).replace(/[^\d.]/g, "")}`
      : "";

  return (
    <div className={`hero-carousel ${mounted ? "loaded" : "loading"}`}>
      <div className="hero-slide">
        {/* LEFT: video / image */}
        <div className="hero-media">
          {heroEmbedUrl ? (
            <div className="hero-video-frame">
              <iframe
                src={heroEmbedUrl}
                title={activeSlide.name}
                allow="autoplay; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          ) : activeSlide.imageUrl ? (
            <div
              className="hero-image-fallback"
              style={{ backgroundImage: `url(${activeSlide.imageUrl})` }}
            ></div>
          ) : (
            <div className="hero-image-fallback hero-image-placeholder">
              Toyco Toys in Action
            </div>
          )}
        </div>

        {/* RIGHT: text + buttons */}
        <div className="hero-inner">
          <div className="hero-text">
            <p className="hero-eyebrow">Featured Toyco Video</p>
            <h1>{activeSlide.name}</h1>

            <p className="hero-meta">
              {activeSlide.category && <span>{activeSlide.category}</span>}
              {activeSlide.ageGroup && <span>Age: {activeSlide.ageGroup}</span>}
              {priceText && <span>{priceText}</span>}
            </p>

            <div className="hero-buttons">
              {activeSlide.marketplaceUrl && (
                <a
                  href={activeSlide.marketplaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="secondary-btn hero-secondary-btn"
                >
                  View Product
                </a>
              )}

              <a
                href="#gallery"
                className="secondary-btn hero-secondary-btn ghost"
              >
                View All Videos
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Dots */}
      <div className="hero-dots">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`hero-dot ${index === current ? "active" : ""}`}
            onClick={() => setCurrent(index)}
          ></button>
        ))}
      </div>
    </div>
  );
}

// Product card (same as before)
function ProductCard({ product, onOpen }) {
  const {
    name,
    category,
    ageGroup,
    price,
    mainImageUrl,
    extraImagesCsv,
    videoUrl,
    marketplaceUrl,
  } = product;

  const formattedPrice =
    price && String(price).trim() !== ""
      ? `₹${String(price).replace(/[^\d.]/g, "")}`
      : null;

  let extraImages = [];
  if (extraImagesCsv && typeof extraImagesCsv === "string") {
    extraImages = extraImagesCsv
      .split(",")
      .map((url) => url.trim())
      .filter((url) => url.length > 0);
  }

  const primaryImage =
    mainImageUrl && mainImageUrl.trim() !== ""
      ? mainImageUrl
      : extraImages.length > 0
      ? extraImages[0]
      : null;

  const thumbnailImages = primaryImage
    ? extraImages.filter((img) => img !== primaryImage).slice(0, 3)
    : extraImages.slice(0, 3);

  // Open modal when clicking image or title
  function handleOpen(e) {
    e.preventDefault();
    if (onOpen) onOpen(product);
  }

  return (
    <div className="product-card">
      {primaryImage ? (
        <img
          src={primaryImage}
          alt={name}
          className="product-image clickable"
          loading="lazy"
          onClick={handleOpen}
          style={{ cursor: "pointer" }}
        />
      ) : (
        <div
          className="product-image-placeholder clickable"
          onClick={handleOpen}
        >
          Image coming soon
        </div>
      )}

      {thumbnailImages.length > 0 && (
        <div className="thumb-row">
          {thumbnailImages.map((thumb, index) => (
            <img
              key={index}
              src={thumb}
              alt={`${name} extra ${index + 1}`}
              className="product-thumb"
              loading="lazy"
              onClick={() => onOpen && onOpen(product)}
              style={{ cursor: "pointer" }}
            />
          ))}
        </div>
      )}

      <h3
        className="product-title clickable"
        onClick={handleOpen}
        style={{ cursor: "pointer" }}
      >
        {name}
      </h3>

      <p className="product-meta">
        {category && <span>{category}</span>}
        {ageGroup && <span>Age: {ageGroup}</span>}
      </p>

      {formattedPrice && <p className="product-price">{formattedPrice}</p>}

      <div className="card-actions">
        {marketplaceUrl && (
          <a
            href={marketplaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="secondary-btn"
          >
            View on Marketplace
          </a>
        )}

        {videoUrl && String(videoUrl).trim() !== "" && (
          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="video-btn"
          >
            Watch Video
          </a>
        )}
      </div>
    </div>
  );
}

function ProductModal({ product, onClose }) {
  <ProductSEO
    product={{
      name: product.name,
      description:
        product.body || product.description || product.shortDescription,
      price: product.price,
      currency: "INR",
      sku: product.sku || product.id || "",
      brand: "Toyco",
      images: [product.mainImageUrl, ...(product.extraImages || [])],
      availability: "https://schema.org/InStock",
      url: product.marketplaceUrl || window.location.href,
    }}
  />;
  const {
    name,
    price,
    category,
    ageGroup,
    mainImageUrl,
    extraImagesCsv,
    videoUrl,
    marketplaceUrl,
  } = product;

  const [activeIndex, setActiveIndex] = useState(0);
  const [playVideo, setPlayVideo] = useState(false);

  let images = [];
  if (mainImageUrl) images.push(mainImageUrl);
  if (extraImagesCsv) {
    const extras = String(extraImagesCsv)
      .split(",")
      .map((u) => u.trim())
      .filter(Boolean);
    images = images.concat(extras);
  }

  const formattedPrice =
    price && String(price).trim() !== ""
      ? `₹${String(price).replace(/[^\d.]/g, "")}`
      : "";

  // video id for embed
  const videoId = getYouTubeId(videoUrl);

  useEffect(() => {
    // lock body scroll when modal open
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div className="product-modal" role="dialog" aria-modal="true">
      <div className="product-modal-inner">
        <button className="modal-close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        <div className="modal-left">
          {/* Image / video area */}
          {!playVideo && images.length > 0 && (
            <div
              className="modal-image"
              style={{ backgroundImage: `url(${images[activeIndex]})` }}
            >
              <button
                className="modal-play"
                onClick={() => (videoId ? setPlayVideo(true) : null)}
              >
                ▶
              </button>
            </div>
          )}

          {playVideo && videoId && (
            <div className="modal-video">
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&rel=0&playsinline=1`}
                title={name}
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                style={{ width: "100%", height: "100%", border: 0 }}
              />
            </div>
          )}

          {/* thumbs */}
          <div className="modal-thumbs">
            {images.map((img, i) => (
              <button
                key={i}
                className={`modal-thumb ${i === activeIndex ? "active" : ""}`}
                onClick={() => {
                  setActiveIndex(i);
                  setPlayVideo(false);
                }}
              >
                <img src={img} alt={`thumb-${i}`} loading="lazy" />
              </button>
            ))}
          </div>
        </div>

        <div className="modal-right">
          <h2>{name}</h2>
          {formattedPrice && <p className="modal-price">{formattedPrice}</p>}
          <p className="modal-meta">
            {category && <span>{category}</span>}
            {ageGroup && <span>Age: {ageGroup}</span>}
          </p>

          {marketplaceUrl && (
            <p style={{ marginTop: 12 }}>
              <a
                href={marketplaceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="primary-btn"
              >
                View on Marketplace
              </a>
            </p>
          )}

          <div style={{ marginTop: 16 }}>
            <button
              className="secondary-btn"
              onClick={() => {
                setPlayVideo(true);
              }}
            >
              Play Video
            </button>
          </div>

          <div style={{ marginTop: 18, color: "#6b7280", fontSize: 13 }}>
            <p>
              <strong>Tip:</strong> Click thumbnails to preview images, or press
              Play to watch the video in the modal.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Video Gallery components (same as before)
function VideoGallery({ videos }) {
  return (
    <div className="video-gallery">
      <div className="gallery-scroll">
        {videos.map((product, index) => (
          <GalleryCard key={index} product={product} />
        ))}
      </div>
    </div>
  );
}

// Replace GalleryCard with:
function GalleryCard({ product }) {
  const { name, videoUrl, marketplaceUrl } = product;
  const videoId = getYouTubeId(videoUrl);
  if (!videoId) return null;

  const thumb = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

  // local state to open modal
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <div className="gallery-card">
        <div
          className="gallery-thumb"
          onClick={() => setOpen(true)}
          style={{ cursor: "pointer" }}
        >
          <img
            src={thumb}
            alt={name}
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
          <div className="play-overlay">►</div>
        </div>
        <div className="gallery-info">
          <p className="gallery-title">{name}</p>
          {marketplaceUrl && (
            <a
              href={marketplaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="gallery-link"
            >
              View Product
            </a>
          )}
        </div>
      </div>

      {open && (
        <div className="video-modal" onClick={() => setOpen(false)}>
          <div
            className="video-modal-inner"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&rel=0&playsinline=1`}
              title={name}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              style={{ width: "100%", height: "100%", border: 0 }}
            />
            <button className="modal-close" onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// export default Home;
